class NewsletterPopup extends ModalDialog {
  constructor() {
    super();
    this.init();
    this.attachEventListeners();
  }

  // Moved all event listeners to a separate method for better organization
  attachEventListeners() {
    this.buttonClose = this.querySelector(".js-button-close");
    this.buttonClose.addEventListener("click", this.closeNewsletter.bind(this));

    if (this.form) {
      this.form.addEventListener("submit", this.handleSubmit.bind(this));
    }
  }

  init() {
    this.delay = this.dataset.delay * 1000;
    this.closed = getCookie("newsletter-closed");
    this.subscribed = getCookie("newsletter-subscribed");
    this.form = this.querySelector(".js-form");
    this.ageVerificationPopup = document.querySelector("age-verification-popup");
    this.survey = this.querySelector("[data-newsletter-survey]");
    this.tagField = this.form ? this.form.querySelector("[data-newsletter-tags]") : null;
    this.customerTags = this.parseCustomerTags(this.dataset.customerTags);
    const extraCustomerTags = this.collectAdditionalCustomerTags();
    if (extraCustomerTags.length > 0) {
      const combinedTags = new Set([...this.customerTags, ...extraCustomerTags]);
      this.customerTags = Array.from(combinedTags);
    }
    this.hasPopupSurveyTag = this.customerTags.includes("popup-survey");
  }

  closeNewsletter() {
    setCookie("newsletter-closed", "true");
    this.hide();
  }

  connectedCallback() {
    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === "true") {
      this.show();
      }
      return;
    }

    this.handlePopupVisibility();
  }

  handlePopupVisibility() {
    if (this.shouldPreventPopup()) {
      return;
    }

    if (getCookie("age-verified") !== "true" && this.ageVerificationPopup) {
      this.handleAgeVerificationPopup();
    } else {
      this.showPopupWithDelay();
    }
  }

  handleAgeVerificationPopup() {
    if (this.shouldPreventPopup()) {
      return;
    }

    let prevClassState = this.classList.contains("newsletter-popup-is-hidden");

    const observer = new MutationObserver(mutations => {
      const mutation = mutations[0];
      if (mutation.attributeName !== "class") return;

      const currentClassState = mutation.target.classList.contains("newsletter-popup-is-hidden");

      if (prevClassState !== currentClassState) {
        prevClassState = currentClassState;
        if (!currentClassState) {
          this.showPopupWithDelay();
        }
      }
    });

    observer.observe(this, { attributes: true });
  }

  showPopupWithDelay() {
    if (this.shouldPreventPopup()) {
      return;
    }

    setTimeout(() => {
      if (this.closed !== "true" && this.subscribed !== "true" && !this.shouldPreventPopup()) {
        this.show();
      }
    }, this.delay);
  }

  handleSubmit(event) {
    if (!this.validateSurvey()) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    this.applySurveyTags();
    setCookie("newsletter-subscribed", "true");
    this.hide();
    this.submitNewsletterForm();
  }

  async submitNewsletterForm() {
    if (!this.form) return;

    const action = this.form.getAttribute("action") || window.location.href;
    const method = (this.form.getAttribute("method") || "post").toUpperCase();
    const formData = new FormData(this.form);

    try {
      const response = await fetch(action, {
        method,
        body: formData,
        credentials: "same-origin",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });

      if (!response.ok) {
        throw new Error("Newsletter request failed");
      }

      this.form.reset();
    } catch (error) {
      if (this.form) {
        this.form.submit();
      }
    }
  }

  validateSurvey() {
    if (!this.survey) return true;

    let isValid = true;
    const questions = Array.from(this.survey.querySelectorAll("[data-newsletter-question]"));

    questions.forEach(question => {
      this.clearQuestionError(question);

      const checkedAnswer = question.querySelector('input[type="radio"]:checked');
      if (!checkedAnswer) {
        isValid = false;
        this.setQuestionError(question);
      }
    });

    return isValid;
  }

  clearQuestionError(question) {
    question.classList.remove("newsletter-popup__question--error");
    const message = question.querySelector("[data-newsletter-question-error]");
    if (message) {
      message.remove();
    }
  }

  setQuestionError(question) {
    question.classList.add("newsletter-popup__question--error");
    const errorMessage = this.getSurveyErrorMessage();

    const helper = document.createElement("p");
    helper.dataset.newsletterQuestionError = "true";
    helper.className = "newsletter-popup__question-error";
    helper.textContent = errorMessage;
    question.appendChild(helper);
  }

  getSurveyErrorMessage() {
    if (this.survey) {
      const errorCopy = this.survey.dataset.surveyError;
      if (errorCopy) {
        return errorCopy;
      }
    }
    return "Please answer this question.";
  }

  applySurveyTags() {
    if (!this.tagField || !this.survey) return;

    const answers = Array.from(this.survey.querySelectorAll('input[data-newsletter-answer]:checked'));
    if (!answers.length) return;

    const currentTags = this.tagField.value
      .split(",")
      .map(tag => tag.trim())
      .filter(Boolean);

    const answerTags = answers
      .map(answer => {
        const question = answer.closest("[data-newsletter-question]");
        if (!question) return null;
        const index = question.dataset.questionIndex;
        const normalizedValue = this.normalizeTagValue(answer.value);
        if (!normalizedValue) return null;
        return `survey-q${index}-${normalizedValue}`;
      })
      .filter(Boolean);

    const uniqueTags = Array.from(new Set([...currentTags, ...answerTags]));
    this.tagField.value = uniqueTags.join(", ");
  }

  normalizeTagValue(value) {
    if (!value) return "";
    return value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  collectAdditionalCustomerTags() {
    const sources = [];

    if (window.theme && window.theme.customer && window.theme.customer.tags) {
      sources.push(window.theme.customer.tags);
    }

    if (window.Shopify && window.Shopify.customerTags) {
      sources.push(window.Shopify.customerTags);
    }

    if (
      window.ShopifyAnalytics &&
      window.ShopifyAnalytics.meta &&
      window.ShopifyAnalytics.meta.page &&
      window.ShopifyAnalytics.meta.page.customerTags
    ) {
      sources.push(window.ShopifyAnalytics.meta.page.customerTags);
    }

    if (window.customerTags) {
      sources.push(window.customerTags);
    }

    return sources
      .map(source => this.parseCustomerTags(source))
      .reduce((accumulator, tags) => accumulator.concat(tags), []);
  }

  parseCustomerTags(tags = "") {
    if (Array.isArray(tags)) {
      return tags
        .map(tag => (typeof tag === "string" ? tag : ""))
        .map(tag => tag.trim().toLowerCase())
        .filter(Boolean);
    }

    if (typeof tags !== "string") {
      return [];
    }

    return tags
      .split(",")
      .map(tag => tag.trim().toLowerCase())
      .filter(Boolean);
  }

  shouldPreventPopup() {
    return this.hasPopupSurveyTag === true;
  }
}

customElements.define("newsletter-popup", NewsletterPopup);
